// /admin/users · user management tab. Gated by requireUserMgmt (admin OR
// can_manage_users). Create + invite, edit, deactivate (with task
// offboarding), reactivate, resend invite, clear lockout.
// Backend: /admin/users* — see worker/src/routes/adminUsers.ts.

import { useState, useEffect, useMemo } from 'react';
import * as api from '../lib/api.ts';
import { canManageUsers, isCsManager } from '../lib/auth.ts';
import { confirmDialog } from '../lib/confirm.js';

const ROLE_LABEL = { admin: '管理员', store_manager: '店长', staff: '员工' };
// Synthetic labels: a staff with a shift is a 领班; a staff with cs_manager is a 客服管理员.
const roleLabel = (u) => (u.role === 'staff' && u.shift)
  ? `领班·${u.shift === 'morning' ? '早班' : '晚班'}`
  : (u.role === 'staff' && u.csManager) ? '客服管理员'
  : (ROLE_LABEL[u.role] || u.role);
const STATE_LABEL = { invited: '待激活', active: '在岗', deactivated: '已停用' };
const DEPTS = ['运营', '客服', '采购', '设计', 'Marketing', '研发'];
const CS_DEPT = '客服';   // the department a 客服管理员 is scoped to
const STORE_CODES = ['RH', 'NY', 'DT'];

const StateBadge = ({ state }) => (
  <span className={`status-badge user-state-${state}`}>{STATE_LABEL[state] || state}</span>
);

// Format the small grey line under each state badge:
//   active   + activatedAt    → "已激活 · 5月23日"
//   active   + no activatedAt → "" (pre-invite seeded — Tony / Jasmine etc.)
//   invited  + pending        → "邀请已发送 · 5月23日 · 未点击"
//   invited  + no pending     → "未邀请"
//   deactivated               → "" (state badge says it all)
function ymd(iso) {
  if (!iso) return '';
  // D1 returns "YYYY-MM-DD HH:MM:SS" (UTC, no T). Parse as UTC, render local.
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}
function inviteStatusLine(u) {
  if (u.state === 'deactivated') return '';
  if (u.state === 'active') {
    return u.activatedAt ? `已激活 · ${ymd(u.activatedAt)}` : '';
  }
  // invited
  if (u.hasPendingInvite && u.inviteSentAt) {
    return `邀请已发送 · ${ymd(u.inviteSentAt)} · 未点击`;
  }
  return '未邀请';
}

// ---------- page ------------------------------------------------------------

export const AdminUsers = ({ user, onStaffChanged }) => {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  // Search + state filter for the new sticky toolbar.
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');     // all | active | invited | deactivated

  async function refresh() {
    try {
      setUsers(await api.listUsers());
      setErr('');
    } catch (e) {
      setErr(e?.message || '加载失败');
    }
  }
  useEffect(() => { refresh(); }, []);

  async function doReactivate(u) {
    if (!await confirmDialog({ title: '重新启用账号', message: `确认重新启用「${u.name}」？账号会回到「待激活」状态，需要重新设置密码。`, confirmLabel: '重新启用' })) return;
    try {
      const r = await api.reactivateUser(u.id);
      await refresh();
      setNotice(!r.has_email
        ? `已重新启用「${u.name}」。该账号无邮箱，请补充邮箱后重发邀请。`
        : r.invite_email_sent
          ? `已重新启用「${u.name}」，邀请邮件已发送。`
          : `已重新启用「${u.name}」，但邀请邮件发送失败 —— 请在列表中重发。`);
    } catch (e) { setErr(e?.message || '操作失败'); }
  }

  async function doResend(u) {
    // For an active user this will also RESET their password — confirm first
    // so a single misclick doesn't kick someone out who's currently using the
    // app. For an invited user it's just a fresh email; no confirm needed.
    if (u.state === 'active') {
      const ok = await confirmDialog({
        title: '重发邀请会重置密码', kind: 'danger', confirmLabel: '发送并重置',
        message: `给「${u.name}」发送邀请邮件会重置 TA 的密码 — TA 必须用邮件里的链接重新设置才能登录。\n\n确认发送？`,
      });
      if (!ok) return;
    }
    try {
      const r = await api.resendInvite(u.id);
      await refresh();
      const tail = r.was_reset
        ? `（已重置密码，TA 需要点击邮件里的链接重新设置）` : '';
      if (r.invite_email_sent) {
        setNotice(`邀请邮件已发送至 ${u.email}。${tail}`);
      } else {
        // Surface the real Resend error so the admin knows what to fix
        // (domain not verified, wrong sender, etc) — beats a generic toast.
        setErr(`邮件发送失败：${r.email_error || '未知错误'}${tail ? ' · ' + tail : ''}`);
      }
    } catch (e) { setErr(e?.message || '操作失败'); }
  }

  async function doInviteAll() {
    const candidates = list.filter(u => u.email && u.state !== 'deactivated' && u.id !== user.id);
    const activeCount = candidates.filter(u => u.state === 'active').length;
    const ok = await confirmDialog({
      title: '批量发送邀请', confirmLabel: `发送给 ${candidates.length} 人`,
      kind: activeCount > 0 ? 'danger' : 'normal',
      message:
        `向 ${candidates.length} 个有邮箱的成员发送邀请邮件。\n` +
        (activeCount > 0
          ? `\n其中 ${activeCount} 人已激活（${candidates.filter(u=>u.state==='active').map(u=>u.name).join('、')}）— 发送会重置 TA 们的密码，必须重新设置才能登录。\n`
          : '') +
        `\n确认发送给全部 ${candidates.length} 人？`,
    });
    if (!ok) return;
    try {
      const r = await api.inviteAll();
      await refresh();
      setNotice(`发送完成 · ${r.sent} 封成功 · ${r.failed} 封失败 · ${r.skipped} 个跳过（共 ${r.total} 人）`);
    } catch (e) { setErr(e?.message || '批量邀请失败'); }
  }

  async function doClearLockout(u) {
    try {
      await api.clearLockout(u.id);
      await refresh();
      setNotice(`已解除「${u.name}」的登录锁定。`);
    } catch (e) { setErr(e?.message || '操作失败'); }
  }

  // Pre-render aggregates + filter (hooks must come BEFORE any early return).
  const list = users ?? [];
  const activeCount = list.filter(u => u.state === 'active').length;
  const invitedCount = list.filter(u => u.state === 'invited').length;
  const deactivatedCount = list.filter(u => u.state === 'deactivated').length;

  // Apply state filter + free-text search across name / email / role / title.
  const visibleUsers = useMemo(() => {
    let rows = list;
    if (stateFilter !== 'all') rows = rows.filter(u => u.state === stateFilter);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.title || '').toLowerCase().includes(q) ||
        (ROLE_LABEL[u.role] || u.role || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [list, stateFilter, search]);

  if (!canManageUsers(user)) {
    return <div className="vava-card empty-state"><div>无权访问此页面</div></div>;
  }
  if (users === null && !err) return <div className="vava-card empty-state">加载中…</div>;

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">账号管理</h1>
          <div className="vava-page-meta">
            共 {list.length} 人 · {activeCount} 在岗 · {invitedCount} 待激活
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={doInviteAll}
                  title="向所有有邮箱的成员发送邀请邮件 · 已激活的会被重置密码">
            ✉ 邀请全员
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ 邀请新用户</button>
        </div>
      </div>

      {notice && (
        <div className="vava-card adm-notice" role="status">
          <span>{notice}</span>
          <button className="adm-notice-close" onClick={() => setNotice('')}>知道了</button>
        </div>
      )}
      {err && <div className="att-error inv-error" role="alert">{err}</div>}

      {/* Sticky toolbar: state filter + search + result count */}
      <div className="inv-toolbar">
        <div className="inv-segmented" role="tablist" aria-label="状态筛选">
          <button role="tab" aria-selected={stateFilter === 'all'}
                  onClick={() => setStateFilter('all')}>全部</button>
          <button role="tab" aria-selected={stateFilter === 'active'}
                  onClick={() => setStateFilter('active')}>
            在岗 {activeCount > 0 && <span className="inv-tree-count">{activeCount}</span>}
          </button>
          <button role="tab" aria-selected={stateFilter === 'invited'}
                  onClick={() => setStateFilter('invited')}>
            待激活 {invitedCount > 0 && <span className="inv-tree-count">{invitedCount}</span>}
          </button>
          <button role="tab" aria-selected={stateFilter === 'deactivated'}
                  onClick={() => setStateFilter('deactivated')}>
            已停用 {deactivatedCount > 0 && <span className="inv-tree-count">{deactivatedCount}</span>}
          </button>
        </div>
        <div className="inv-search">
          <input type="text"
                 value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 aria-label="搜索成员"
                 placeholder="搜 姓名 / 邮箱 / 角色 / 职位…" />
        </div>
        <span className="vava-hint" style={{ marginLeft: 'auto' }}>
          {visibleUsers.length} / {list.length}
        </span>
      </div>

      <div className="inv-grid-wrap">
        {visibleUsers.length === 0 ? (
          <div className="inv-empty">
            <div className="emoji">👥</div>
            <div className="title">
              {search ? `没有匹配「${search}」的成员` : '该状态下没有成员'}
            </div>
            <div className="sub">调整筛选或清空搜索框</div>
          </div>
        ) : (
        <table className="inv-grid">
          <thead>
            <tr>
              <th>姓名</th>
              <th>角色</th>
              <th>状态</th>
              <th>邮箱</th>
              <th className="num">操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map(u => (
              <tr key={u.id}>
                <td style={{ fontWeight: 600 }}>
                  {u.name}
                  {u.canManageUsers && u.role !== 'admin' && (
                    <span className="status-badge user-state-invited"
                          style={{ marginLeft: 8, fontWeight: 500 }}>可管理用户</span>
                  )}
                </td>
                <td>{roleLabel(u)}</td>
                <td>
                  <StateBadge state={u.state} />
                  {u.locked && (
                    <span style={{ marginLeft: 6, color: 'var(--vava-red)', fontSize: 12 }}>
                      🔒 已锁定
                    </span>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--vava-text-muted)', marginTop: 2 }}>
                    {inviteStatusLine(u)}
                  </div>
                </td>
                <td className="mono" style={{ color: u.email ? undefined : 'var(--vava-text-subtle)' }}>
                  {u.email || '无邮箱'}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {u.locked && (
                    <button className="raci-edit-btn" onClick={() => doClearLockout(u)}>解除锁定</button>
                  )}
                  {/* Invite-email button: visible for invited OR active users with email.
                      For active users, doResend() shows a confirm because it resets password.
                      Hidden on the admin's own row — sending to yourself would reset
                      your own password and kill your session mid-action. */}
                  {u.state !== 'deactivated' && u.email && !(u.state === 'active' && u.id === user.id) && (
                    <button className="raci-edit-btn" onClick={() => doResend(u)}
                            title={u.state === 'active'
                              ? '发送邀请邮件 · 会重置 TA 的密码'
                              : '重新发送邀请邮件'}>
                      {u.state === 'invited' ? '重发邀请' : '发邀请'}
                    </button>
                  )}
                  {u.state === 'deactivated' ? (
                    <button className="raci-edit-btn" onClick={() => doReactivate(u)}>重新启用</button>
                  ) : (
                    <>
                      <button className="raci-edit-btn" onClick={() => setEditing(u)}>编辑</button>
                      <button className="raci-edit-btn" style={{ color: 'var(--vava-red)' }}
                              disabled={u.id === user.id}
                              title={u.id === user.id ? '不能停用自己的账号' : undefined}
                              onClick={() => setDeactivating(u)}>停用</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {showCreate && (
        <CreateUserModal onClose={() => setShowCreate(false)} viewerCs={isCsManager(user)}
                         onSaved={() => { refresh(); onStaffChanged?.(); }} />
      )}
      {editing && (
        <EditUserModal user={editing} onClose={() => setEditing(null)} viewerCs={isCsManager(user)}
                       onSaved={() => { refresh(); onStaffChanged?.(); }} />
      )}
      {deactivating && (
        <DeactivateModal
          target={deactivating}
          activeStaff={list.filter(u => u.active && u.id !== deactivating.id)}
          onClose={() => setDeactivating(null)}
          onSaved={(msg) => { setNotice(msg); refresh(); onStaffChanged?.(); }} />
      )}
    </>
  );
};

// ---------- shared form fields ----------------------------------------------

const UserFields = ({ form, update, disabled, lockCs = false }) => {
  const isStaff = form.role === 'staff';
  const isAdmin = form.role === 'admin';
  const storesAll = form.stores.includes('ALL');
  // 领班 / 客服管理员 are synthetic dropdown choices layered on role 'staff' + a
  // capability (shift / csManager). Keeps the role narrow (no 店长/admin powers).
  const roleChoice = (form.role === 'staff' && form.shift) ? 'shift_lead'
    : (form.role === 'staff' && form.csManager) ? 'cs_manager'
    : form.role;

  function toggleStore(code) {
    if (code === 'ALL') {
      update({ stores: storesAll ? [] : ['ALL'] });
      return;
    }
    const base = form.stores.filter(x => x !== 'ALL');
    update({ stores: base.includes(code) ? base.filter(x => x !== code) : [...base, code] });
  }

  return (
    <>
      <div className="vava-field">
        <label className="vava-label">姓名 *</label>
        <input className="vava-input" value={form.name} disabled={disabled}
               onChange={(e) => update({ name: e.target.value })} />
      </div>
      <div className="vava-field">
        <label className="vava-label">角色 *</label>
        <select className="vava-select" value={lockCs ? 'staff' : roleChoice} disabled={disabled || lockCs}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'shift_lead') update({ role: 'staff', shift: form.shift || 'morning', csManager: false, canManageUsers: false });
                  else if (v === 'cs_manager') update({ role: 'staff', shift: '', csManager: true, dept: '客服', canManageUsers: false });
                  else update({ role: v, shift: '', csManager: false, canManageUsers: v === 'staff' ? false : form.canManageUsers });
                }}>
          <option value="staff">员工</option>
          {!lockCs && <option value="shift_lead">领班</option>}
          {!lockCs && <option value="cs_manager">客服管理员</option>}
          {!lockCs && <option value="store_manager">店长</option>}
          {!lockCs && <option value="admin">管理员</option>}
        </select>
        {lockCs && <div style={{ fontSize: 12, color: 'var(--vava-text-muted)', marginTop: 2 }}>客服管理员只能创建客服部门的普通员工。</div>}
      </div>
      <div className="vava-field">
        <label className="vava-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox"
                 checked={isAdmin || form.canManageUsers}
                 disabled={disabled || isStaff || isAdmin}
                 onChange={(e) => update({ canManageUsers: e.target.checked })} />
          可管理用户（超级管理员权限）
        </label>
        <div style={{ fontSize: 12, color: 'var(--vava-text-muted)', marginTop: 2 }}>
          {isAdmin ? '管理员默认拥有此权限。'
            : isStaff ? '普通员工不能被授予此权限。'
            : '店长可被授予用户管理权限。'}
        </div>
      </div>
      <div className="vava-field">
        <label className="vava-label">邮箱</label>
        <input className="vava-input" type="email" value={form.email} disabled={disabled}
               placeholder="用于发送邀请 / 通知邮件"
               onChange={(e) => update({ email: e.target.value })} />
      </div>
      <div className="vava-field">
        <label className="vava-label">职务</label>
        <input className="vava-input" value={form.title} disabled={disabled}
               placeholder="如：RH 店长"
               onChange={(e) => update({ title: e.target.value })} />
      </div>
      <div className="vava-field">
        <label className="vava-label">部门</label>
        <select className="vava-select" value={lockCs ? CS_DEPT : (form.dept || '')} disabled={disabled || lockCs}
                onChange={(e) => update({ dept: e.target.value || null })}>
          <option value="">（无）</option>
          {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      {!lockCs && (
      <div className="vava-field">
        <label className="vava-label">门店</label>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={storesAll} disabled={disabled}
                   onChange={() => toggleStore('ALL')} /> 全部
          </label>
          {STORE_CODES.map(code => (
            <label key={code} style={{ display: 'flex', alignItems: 'center', gap: 6,
                                        opacity: storesAll ? 0.4 : 1 }}>
              <input type="checkbox" disabled={disabled || storesAll}
                     checked={!storesAll && form.stores.includes(code)}
                     onChange={() => toggleStore(code)} /> {code}
            </label>
          ))}
        </div>
      </div>
      )}
      {roleChoice === 'shift_lead' && (
        <div className="vava-field">
          <label className="vava-label">班次 *（领班负责的班）</label>
          <select className="vava-select" value={form.shift || 'morning'} disabled={disabled}
                  onChange={(e) => update({ shift: e.target.value })}>
            <option value="morning">早班</option>
            <option value="evening">晚班</option>
          </select>
          <div style={{ fontSize: 12, color: 'var(--vava-text-muted)', marginTop: 2 }}>
            领班只能在「人事档案」里查看本店且同班次员工的薪资，不能编辑。
          </div>
        </div>
      )}
    </>
  );
};

/** Effective can_manage_users for the API: never true for plain staff. */
function effectiveCanMgmt(form) {
  if (form.role === 'staff') return false;
  if (form.role === 'admin') return true;
  return !!form.canManageUsers;
}

// ---------- create modal ----------------------------------------------------

const CreateUserModal = ({ onClose, onSaved, viewerCs = false }) => {
  const [form, setForm] = useState({
    name: '', role: 'staff', canManageUsers: false, shift: '', csManager: false,
    email: '', title: '', dept: viewerCs ? CS_DEPT : null, stores: []
  });
  const update = (patch) => setForm(f => ({ ...f, ...patch }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);   // post-submit phase
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  async function submit() {
    setErr('');
    if (!form.name.trim()) return setErr('姓名不能为空');
    setSaving(true);
    try {
      const res = await api.createUser({
        name: form.name.trim(),
        role: form.role,
        email: form.email.trim() || undefined,
        title: form.title.trim() || undefined,
        dept: form.dept || null,
        stores: form.stores,
        can_manage_users: effectiveCanMgmt(form),
        shift: form.role === 'staff' ? (form.shift || null) : null,
        cs_manager: !!form.csManager
      });
      // Clean success (invite delivered) → refresh + close immediately.
      if (res.has_email && res.invite_email_sent) {
        onSaved();
        onClose();
        return;
      }
      setResult(res);          // no-email, or email-send-failed → show result
    } catch (e) {
      setErr(e?.message || '创建失败');
    } finally {
      setSaving(false);
    }
  }

  async function resend() {
    if (!result) return;
    setResending(true);
    setErr('');
    try {
      const r = await api.resendInvite(result.user.id);
      setResent(r.invite_email_sent);
      if (!r.invite_email_sent) setErr('邮件仍然发送失败，请稍后在列表中再次尝试。');
    } catch (e) {
      setErr(e?.message || '重发失败');
    } finally {
      setResending(false);
    }
  }

  function finish() { onSaved(); onClose(); }

  // ---- post-submit result phase ----
  if (result) {
    const name = result.user.name;
    return (
      <div className="vava-modal-backdrop" onClick={finish}>
        <div className="vava-modal" onClick={(e) => e.stopPropagation()}>
          <div className="vava-modal-head">
            <h2>用户已创建</h2>
            <button className="vava-modal-close" onClick={finish}>×</button>
          </div>
          <div className="vava-modal-body">
            {!result.has_email ? (
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                已创建用户「{name}」。该用户暂无邮箱，无法发送邀请邮件。
                补充邮箱后，可在列表中点「重发邀请」。
              </p>
            ) : resent ? (
              <p style={{ margin: 0, color: 'var(--vava-green)' }}>
                邀请邮件已成功发送至 {result.user.email}。
              </p>
            ) : (
              <>
                <p style={{ margin: '0 0 12px', lineHeight: 1.6 }}>
                  已创建用户「{name}」，但邀请邮件发送失败。
                </p>
                <button className="btn btn-secondary" onClick={resend} disabled={resending}>
                  {resending ? '发送中…' : '重新发送邀请'}
                </button>
              </>
            )}
            {err && <div className="att-error" role="alert" style={{ marginTop: 12 }}>{err}</div>}
          </div>
          <div className="vava-modal-foot">
            <button className="btn btn-primary" onClick={finish}>完成</button>
          </div>
        </div>
      </div>
    );
  }

  // ---- form phase ----
  return (
    <div className="vava-modal-backdrop" onClick={onClose}>
      <div className="vava-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vava-modal-head">
          <h2>邀请新用户</h2>
          <button className="vava-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="vava-modal-body">
          <UserFields form={form} update={update} disabled={saving} lockCs={viewerCs} />
          <div style={{ fontSize: 12, color: 'var(--vava-text-muted)' }}>
            填写邮箱后，会自动发送一封设置密码的邀请邮件；新用户设置密码后即可登录。
          </div>
          {err && <div className="att-error" role="alert" style={{ marginTop: 12 }}>{err}</div>}
        </div>
        <div className="vava-modal-foot">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>取消</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? '创建中…' : '创建并邀请'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- edit modal ------------------------------------------------------

const EditUserModal = ({ user, onClose, onSaved, viewerCs = false }) => {
  const [form, setForm] = useState({
    name: user.name, role: user.role, canManageUsers: user.canManageUsers,
    email: user.email || '', title: user.title || '', shift: user.shift || '',
    csManager: !!user.csManager, dept: user.dept, stores: user.stores || []
  });
  const update = (patch) => setForm(f => ({ ...f, ...patch }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function submit() {
    setErr('');
    if (!form.name.trim()) return setErr('姓名不能为空');
    setSaving(true);
    try {
      // 客服管理员 viewers may only change profile basics — the server rejects the
      // rest, so don't send them.
      const payload = viewerCs
        ? { name: form.name.trim(), email: form.email.trim() || null, title: form.title.trim() }
        : {
            name: form.name.trim(),
            role: form.role,
            email: form.email.trim() || null,
            title: form.title.trim(),
            dept: form.dept || null,
            stores: form.stores,
            can_manage_users: effectiveCanMgmt(form),
            shift: form.role === 'staff' ? (form.shift || null) : null,
            cs_manager: !!form.csManager
          };
      await api.updateUser(user.id, payload);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="vava-modal-backdrop" onClick={onClose}>
      <div className="vava-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vava-modal-head">
          <h2>编辑用户 · {user.name}</h2>
          <button className="vava-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="vava-modal-body">
          <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--vava-text-muted)' }}>
            状态：<StateBadge state={user.state} />
            <span style={{ marginLeft: 8 }}>
              （状态变更请用「停用」/「重新启用」）
            </span>
          </div>
          <UserFields form={form} update={update} disabled={saving} lockCs={viewerCs} />
          {err && <div className="att-error" role="alert" style={{ marginTop: 12 }}>{err}</div>}
        </div>
        <div className="vava-modal-foot">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>取消</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- deactivate modal (offboarding) ----------------------------------

const DeactivateModal = ({ target, activeStaff, onClose, onSaved }) => {
  const [detail, setDetail] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [assign, setAssign] = useState({});      // task_id → new R staff_id
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.getUser(target.id)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setLoadErr(e?.message || '加载失败'); });
    return () => { cancelled = true; };
  }, [target.id]);

  const openTasks = detail?.open_r_tasks ?? [];
  const tplCount = detail?.recurring_templates?.length ?? 0;
  const allAssigned = openTasks.every(t => assign[t.id]);

  async function confirm() {
    setErr('');
    if (!allAssigned) return setErr('请为每个进行中的待办指定新的负责人。');
    setSaving(true);
    try {
      const r = await api.deactivateUser(target.id, assign);
      onSaved(`已停用「${target.name}」` +
        (r.reassigned_tasks > 0 ? ` · ${r.reassigned_tasks} 个待办已转交。` : '。'));
      onClose();
    } catch (e) {
      setErr(e?.status === 422
        ? '仍有待办未指定负责人，请检查。'
        : (e?.message || '停用失败'));
      setSaving(false);
    }
  }

  return (
    <div className="vava-modal-backdrop" onClick={onClose}>
      <div className="vava-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vava-modal-head">
          <h2>停用 · {target.name}</h2>
          <button className="vava-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="vava-modal-body">
          <p style={{ margin: '0 0 12px', lineHeight: 1.6 }}>
            停用后，该用户将无法登录，其所有登录会话与推送订阅会被清除，
            未使用的邀请链接将失效。
          </p>

          {loadErr && <div className="att-error" role="alert">{loadErr}</div>}
          {!detail && !loadErr && (
            <div style={{ color: 'var(--vava-text-muted)' }}>正在检查交接事项…</div>
          )}

          {detail && (
            <>
              {openTasks.length > 0 ? (
                <>
                  <div style={{ fontWeight: 600, margin: '8px 0' }}>
                    以下 {openTasks.length} 个进行中的待办由 TA 负责，请指定新的负责人：
                  </div>
                  {openTasks.map(t => (
                    <div key={t.id} className="vava-field">
                      <label className="vava-label">
                        <span className="mono">{t.id}</span> · {t.title}
                      </label>
                      <select className="vava-select"
                              value={assign[t.id] || ''}
                              disabled={saving}
                              onChange={(e) => setAssign(a => ({ ...a, [t.id]: e.target.value }))}>
                        <option value="">— 选择新负责人 —</option>
                        {activeStaff.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ color: 'var(--vava-text-muted)', margin: '8px 0' }}>
                  该用户没有进行中的负责待办，无需交接。
                </div>
              )}

              {tplCount > 0 && (
                <div style={{ fontSize: 13, color: 'var(--vava-text-muted)', marginTop: 8 }}>
                  TA 名下的 {tplCount} 个周期待办模板将转交给你。
                </div>
              )}
            </>
          )}

          {err && <div className="att-error" role="alert" style={{ marginTop: 12 }}>{err}</div>}
        </div>
        <div className="vava-modal-foot">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>取消</button>
          <button className="btn btn-primary"
                  style={{ background: 'var(--vava-red)' }}
                  onClick={confirm}
                  disabled={saving || !detail || !allAssigned}>
            {saving ? '停用中…' : '确认停用'}
          </button>
        </div>
      </div>
    </div>
  );
};
